import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Icon, StackPanel, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { TextBoxField } from '@agilepoint/wat-react-ux-form-fields';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { IChangePasswordFields } from 'modules/login/types';
import { useValidatePasswordHook } from 'lib/hooks/validate-password-hook';
import { IChangePasswordPayload } from 'modules/shared/queries';
import { CenteredSpinner, clearCookies, StyledPrimaryButton } from 'common';
import { useConfigScopeSelectors } from 'modules/core/scopes';
import { useTranslate } from '@agilepoint/wat-react';

interface IChangePasswordControlProps {
    changePasswordHandler: (formData: IChangePasswordPayload) => Promise<any>;
}

const StyledForm = styled.form`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 380px;
    height: 100%;
`;

const LoginControlsBaseWrapper = styled(StackPanel)`
    margin-left 80px;
    justify-content: center;
    align-items: center;
    display: grid;
    width: 345px;
    row-gap:5px;
`;


const StyledFormInputs = styled(StackPanel)`
    padding-top: 20px;
`;

const RelativeDiv = styled.div`
    position: relative;
`;

const StyledTextBoxField = styled(TextBoxField)`
    .ap-textbox-wrapper {
        width: 350px;
        height: 40px;
        padding:0px;
        .ap-textbox{
            padding:0px 16px !important;
        }
    }
    .ap-formField-label {
        margin-bottom:10px;
    }
    .ap-formField-label span {
        font-size: 16px;
        font-weight:500;
        line-height: 19px;
        color:${({ theme: { semantics: { palette } } }) => palette.neutralPrimary};
    }
    }
`;

const ActionButtonsWrapper = styled(StackPanel)`
    margin-top: 20px;
`;

const ChangePasswordButton = styled(StyledPrimaryButton)`
    margin-right:20px;
    height: 40px;
    cursor: pointer;
    background-color: ${({ theme }) => theme.semantics.palette.themePrimary};
    border: 0 ${({ theme }) => theme.semantics.palette.themePrimary};
    border-radius: 22px
    color: ${({ theme }) => theme.semantics.palette.white};
    font-size: 16px;
    font-weight: 500;
    line-height: 19px;
`;

const ShowPasswordIcon = styled(Icon) <{ $isError?: string }>`
    position: absolute;
    right: 16px;
    top:40px;
    fill: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.eyeIconfillColor};
    cursor: pointer;
`;

const ChangeControlsBase = React.memo((props: IChangePasswordControlProps) => {
    const translate = useTranslate();
    const { changePasswordHandler } = props;
    const [isSavingData, setIsSavingData] = React.useState(false);
    const { showNotifications } = useNotificationEvents();
    const { selectBasePathURL } = useConfigScopeSelectors('config');
    const baseURL = selectBasePathURL();

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const onShowCurrentPasswordClick = React.useCallback(() => (setShowCurrentPassword((prevValue) => !prevValue)), []);
    const onShowNewPasswordClick = React.useCallback(() => (setShowNewPassword((prevValue) => !prevValue)), []);
    const onConfirmPasswordClick = React.useCallback(() => (setShowConfirmPassword((prevValue) => !prevValue)), []);

    const { formState, handleSubmit, watch } = useFormContext<IChangePasswordFields>();
    const confirmPassword = watch('confirmPassword');
    const { validatePasswordPattern, validatePasswordMatch } = useValidatePasswordHook({ password: watch('newPassword'), confirmPassword });

    const onFormSubmit = useCallback((formData: IChangePasswordFields) => {
        setIsSavingData(true);
        changePasswordHandler({
            newPassword: formData.newPassword,
            oldPassword: formData.currentPassword
        }).then((res) => {
            setIsSavingData(false);
            if (res?.ChangeUserPasswordResult) {
                showNotifications({ message: 'Password changed successfully.' });
                clearCookies();
                window.open(baseURL, '_self');
            }
            else {
                throw new Error('Error');
            }
        }).catch(() => {
            setIsSavingData(false);
            // Note: need to localize this text
            showNotifications({ message: 'Error - Something went wrong!' });
        });
    }, [baseURL, changePasswordHandler, showNotifications]);

    return (
        <LoginControlsBaseWrapper orientation="vertical" justifyContent="center">
            <StyledFormInputs orientation="vertical">
                <RelativeDiv>
                    <StyledTextBoxField
                        name="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        label={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.currentPassword')}
                        placeholder={translate('modules.createNewApp.steps.basicSettings.changePassword.placeHolders.currentPassword')}
                        title={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.currentPassword')}
                        validationRules={{
                            required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredMessage.currentPasswordRequired')
                        }} />
                    <ShowPasswordIcon
                        iconName={showCurrentPassword ? 'ap-eye.solid' : 'ap-hide-preview.solid'}
                        onClick={onShowCurrentPasswordClick}
                        $isError={formState.errors.currentPassword?.message} />
                </RelativeDiv>

                <RelativeDiv>
                    <StyledTextBoxField
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        label={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.newPassword')}
                        placeholder={translate('modules.createNewApp.steps.basicSettings.changePassword.placeHolders.newPassword')}
                        title={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.newPassword')}
                        validationRules={{
                            required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredMessage.newPasswordRequired'),
                            validate: validatePasswordPattern
                        }} />
                    <ShowPasswordIcon
                        iconName={showNewPassword ? 'ap-eye.solid' : 'ap-hide-preview.solid'}
                        onClick={onShowNewPasswordClick}
                        $isError={formState.errors.newPassword?.message} />
                </RelativeDiv>

                <RelativeDiv>
                    <StyledTextBoxField
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        label={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.confirmPassword')}
                        placeholder={translate('modules.createNewApp.steps.basicSettings.changePassword.placeHolders.confirmPassword')}
                        title={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.confirmPassword')}
                        validationRules={{
                            required: translate('modules.createNewApp.steps.basicSettings.validationMessages.requiredMessage.confirmPasswordRequired'),
                            validate: validatePasswordMatch

                        }}
                    />
                    <ShowPasswordIcon
                        iconName={showConfirmPassword ? 'ap-eye.solid' : 'ap-hide-preview.solid'}
                        onClick={onConfirmPasswordClick}
                        $isError={formState.errors.confirmPassword?.message} />
                </RelativeDiv>
            </StyledFormInputs>
            <ActionButtonsWrapper width="100%" orientation="horizontal" justifyContent="end">
                {isSavingData ?
                    <CenteredSpinner spinnerType="indeterminate" /> :
                    <ChangePasswordButton
                        label={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.buttonLabel')}
                        title={translate('modules.createNewApp.steps.basicSettings.changePassword.labels.buttonLabel')}
                        type='submit'
                        disabled={!formState.isValid}
                        onClick={handleSubmit(onFormSubmit)} />}
            </ActionButtonsWrapper>
        </LoginControlsBaseWrapper>
    );
});

export const ChangePasswordControl = (props: IChangePasswordControlProps) => {
    const methods = useForm<IChangePasswordFields>({
        mode: 'onBlur'
    });

    return (
        <FormProvider {...methods}>
            <StyledForm>
                <ChangeControlsBase changePasswordHandler={props.changePasswordHandler} />
            </StyledForm>
        </FormProvider>
    );
};
